import AcademicYearBadge from "./AcademicYearBadge";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { RiBarChartBoxLine } from "react-icons/ri";

const API_BASE = "https://admin-back-j3j4.onrender.com/api";
const API = {
  TEACHING: `${API_BASE}/office-user/office-details/teaching`,
  NON_TEACHING: `${API_BASE}/office-user/office-details/nonteaching`,
  OFFICE_DEPT_GET: `${API_BASE}/office/officedept/get`,
  OFFICE_DEPT_ACAD_YEAR: `${API_BASE}/office/teaching-staff/academic-year`
};

// Fixed render order for minority/general subcategories in 2x2 layout
const SUBCATEGORY_ORDER = [
  'General Category',
  'PwBD',
  'Muslim Minority',
  'Other Minority'
];

export default function OfficeDetails() {
  const [activeTab, setActiveTab] = useState("teaching");
  const [teachingData, setTeachingData] = useState([]);
  const [nonTeachingData, setNonTeachingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewRow, setPreviewRow] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [breakdownGroup, setBreakdownGroup] = useState(null);
  const [activeMinorityTab, setActiveMinorityTab] = useState("PwBD");
  // Department detail modal state (used by Department Entry cards)
  const [detailRow, setDetailRow] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const recordsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  // Add serial base for S.No across pages
  const serialBase = (currentPage - 1) * recordsPerPage;
  const [latestAcademicYear, setLatestAcademicYear] = useState("");
  const [detailRecords, setDetailRecords] = useState([]); // detailed non-teaching rows

  // --- New: department entry state & options (referenced from officedeptenrollment.js) ---
  const DEPT_OPTIONS = [
    'B.C.A', 'B.A ENGLISH', 'M.A ENGLISH', 'BBA', 'B.COM',
    'B.SC MATHS', 'M.SC MATHS', 'B.SC Physics', 'M.Com', 'B.Sc Chemistry'
  ];
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedDeptAcademicYear, setSelectedDeptAcademicYear] = useState('');
  const [deptDetailRecords, setDeptDetailRecords] = useState([]); // raw rows from /office/officedept/get
  const [deptTotals, setDeptTotals] = useState({
    'General Category': { male: 0, female: 0, transgender: 0 },
    'PwBD': { male: 0, female: 0, transgender: 0 },
    'Muslim Minority': { male: 0, female: 0, transgender: 0 },
    'Other Minority': { male: 0, female: 0, transgender: 0 }
  });
  // --- end new ---

  useEffect(() => {
    setLoading(true);
    axios.get(API.TEACHING).then(res => setTeachingData(res.data.data || [])).catch(() => setTeachingData([]));
    axios.get(API.NON_TEACHING).then(res => setNonTeachingData(res.data.data || [])).catch(() => setNonTeachingData([])).finally(() => setLoading(false));
    // fetch detailed non-teaching rows (same endpoint used by office UI)
    axios.get("https://admin-back-j3j4.onrender.com/api/office/non-teaching-staff")
      .then(res => setDetailRecords(res.data.data || []))
      .catch(() => setDetailRecords([]));
  }, []);

  useEffect(() => {
    async function fetchAdminAcademicYear() {
      try {
        const res = await axios.get("https://admin-back-j3j4.onrender.com/api/admin/all", {
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
        });
        if (res.data?.admins?.length) {
          setLatestAcademicYear(res.data.admins[0].academic_year || "");
        }
      } catch {
        setLatestAcademicYear("");
      }
    }
    fetchAdminAcademicYear();
  }, []);

  // ensure department-entry year follows admin/latestAcademicYear by default
  useEffect(() => {
    setSelectedDeptAcademicYear(latestAcademicYear || '');
  }, [latestAcademicYear]);

  // Fetch department-level records when department or year changes
  useEffect(() => {
    if (!selectedDept) {
      setDeptDetailRecords([]);
      setDeptTotals({
        'General Category': { male: 0, female: 0, transgender: 0 },
        'PwBD': { male: 0, female: 0, transgender: 0 },
        'Muslim Minority': { male: 0, female: 0, transgender: 0 },
        'Other Minority': { male: 0, female: 0, transgender: 0 }
      });
      return;
    }
    // call async fetch
    (async () => {
      const year = selectedDeptAcademicYear || latestAcademicYear || '';
      try {
        setLoading(true);
        const res = await axios.get(API.OFFICE_DEPT_GET, {
          params: { academic_year: year, department: selectedDept },
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
        });
        const rows = res.data?.rows || [];
        setDeptDetailRecords(rows);
        setDeptTotals(computeDeptTotalsFromRows(rows));
      } catch (err) {
        console.debug("Failed to fetch dept records", err);
        setDeptDetailRecords([]);
        setDeptTotals({
          'General Category': { male: 0, female: 0, transgender: 0 },
          'PwBD': { male: 0, female: 0, transgender: 0 },
          'Muslim Minority': { male: 0, female: 0, transgender: 0 },
          'Other Minority': { male: 0, female: 0, transgender: 0 }
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedDept, selectedDeptAcademicYear, latestAcademicYear]);

  // Robust aggregator for department entry totals
  function computeDeptTotalsFromRows(rows = []) {
    const totals = { 'General Category': { male: 0, female: 0, transgender: 0 }, 'PwBD': { male: 0, female: 0, transgender: 0 }, 'Muslim Minority': { male: 0, female: 0, transgender: 0 }, 'Other Minority': { male: 0, female: 0, transgender: 0 } };
    rows.forEach(r => {
      const sub = (r.subcategory_name || r.subcategory || r.sub || '').trim();
      if (!sub || !totals[sub]) return;
      // prefer aggregated gender fields when present
      if (typeof r.male_count !== 'undefined' || typeof r.female_count !== 'undefined' || typeof r.transgender_count !== 'undefined') {
        totals[sub].male += Number(r.male_count || 0);
        totals[sub].female += Number(r.female_count || 0);
        totals[sub].transgender += Number(r.transgender_count || 0);
        return;
      }
      const gender = String(r.gender_name || r.gender || (r.gender_id ? ({ 1: 'Male', 2: 'Female', 3: 'Transgender' }[r.gender_id]) : '')).trim();
      const cnt = Number(r.count ?? r.filled_count ?? 0) || 0;
      if (/male/i.test(gender)) totals[sub].male += cnt;
      else if (/female/i.test(gender)) totals[sub].female += cnt;
      else totals[sub].transgender += cnt;
    });
    return totals;
  }

  // Compute category-level breakdown for the currently selected department + subcategory.
  function computeDeptCategoryBreakdown(subcategoryName) {
    if (!subcategoryName) return [];

    const getStartYear = (y) => {
      if (!y) return "";
      const m = String(y).match(/\d{4}/);
      return m ? m[0] : "";
    };

    const normalize = (s) => String(s || "").trim();
    const selYear = getStartYear(selectedDeptAcademicYear || latestAcademicYear || "");
    const selDept = normalize(selectedDept);
    const subNameNormalized = normalize(subcategoryName);

    const rows = (deptDetailRecords || []).filter(r => {
      const rowYear = getStartYear(r.academic_year || r.academicYear || r.academic || "");
      if (selYear && rowYear && rowYear !== selYear) return false;
      const rowDept = normalize(r.department || r.dept || r.department_name);
      if (selDept && rowDept !== selDept) return false;
      const rowSub = normalize(r.subcategory_name || r.subcategory || r.sub || r.subcategory_name);
      if (!rowSub) return false;
      return rowSub === subNameNormalized;
    });

    // Always render the master categories (and append any extra unknowns)
    const categoriesFromRows = Array.from(
      new Set(rows.map(r => normalize(r.category_name || r.category)).filter(Boolean))
    );
    const cats = [...MASTER_CATEGORIES, ...categoriesFromRows.filter(c => !MASTER_CATEGORIES.includes(c))];

    const map = {};
    cats.forEach(cat => (map[cat] = { male: 0, female: 0, transgender: 0 }));

    rows.forEach(r => {
      const cat = normalize(r.category_name || r.category) || "Other";
      if (!map[cat]) map[cat] = { male: 0, female: 0, transgender: 0 };

      if (typeof r.male_count !== "undefined" || typeof r.female_count !== "undefined" || typeof r.transgender_count !== "undefined") {
        map[cat].male += Number(r.male_count || 0);
        map[cat].female += Number(r.female_count || 0);
        map[cat].transgender += Number(r.transgender_count || 0);
        return;
      }

      const gender = String(r.gender_name || r.gender || (r.gender_id ? ({1:'Male',2:'Female',3:'Transgender'}[r.gender_id]) : "")).trim();
      const cnt = Number(r.count ?? r.filled_count ?? r.filled ?? r.value ?? 0) || 0;
      if (/male/i.test(gender)) map[cat].male += cnt;
      else if (/female/i.test(gender)) map[cat].female += cnt;
      else map[cat].transgender += cnt;
    });

    return Object.keys(map).map(category => ({ category, ...map[category] }));
  }
  // end computeDeptCategoryBreakdown

  // --- CHANGED: derive filtered arrays by latestAcademicYear and use them for rendering ---
  // Filter nonTeachingData by latest academic year
  const filteredNonTeachingData = nonTeachingData.filter(
    row => row.academic_year === latestAcademicYear
  );

  // Filter teachingData by latest academic year
  const filteredTeachingData = teachingData.filter(
    row => row.academic_year === latestAcademicYear
  );

  // Filter detailed rows by latest academic year (used for breakdown popup)
  const filteredDetailRecords = detailRecords.filter(r => r.academic_year === latestAcademicYear);

  // Group by staff_group for summary (use filteredNonTeachingData now)
  const groupMap = {};
  filteredNonTeachingData.forEach(row => {
    const group = row.staff_group || 'Other';
    if (!groupMap[group]) groupMap[group] = [];
    groupMap[group].push(row);
  });

  // Paginate filtered teaching data
  const paginatedTeaching = filteredTeachingData.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  const totalPagesTeaching = Math.ceil(filteredTeachingData.length / recordsPerPage);

  function renderTeachingPagination() {
    if (totalPagesTeaching <= 1) return null;
    const pages = [];
    for (let i = 1; i <= totalPagesTeaching; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-2 rounded-lg border font-semibold mx-1 ${i === currentPage ? 'bg-blue-700 text-white shadow' : 'bg-white text-gray-700 hover:bg-blue-50'}`}
        >
          {i}
        </button>
      );
    }
    return (
      <div className="flex items-center justify-center mt-6 mb-2">
        <button
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className={`px-3 py-2 rounded-lg border font-semibold mx-1 ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-blue-50'}`}
        >
          &lt; Back
        </button>
        {pages}
        <button
          onClick={() => setCurrentPage((p) => Math.min(totalPagesTeaching, p + 1))}
          disabled={currentPage === totalPagesTeaching}
          className={`px-3 py-2 rounded-lg border font-semibold mx-1 ${currentPage === totalPagesTeaching ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-blue-50'}`}
        >
          Next &gt;
        </button>
      </div>
    );
  }

  // Helper to format date as DD-MM-YYYY
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return `${d.getDate().toString().padStart(2, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getFullYear()}`;
  };
// Master list of categories (update as per your actual categories)
  const MASTER_CATEGORIES = [
    "General Including EWS",
    "Scheduled Caste (SC)",
    "Scheduled Tribe (ST)",
    "Other Backward Classes (OBC)"
  ];
  // Helper: breakdown counts for selected group and subcategory
  // Use detailed rows for accurate per-category & per-gender breakdown.
  function getMinorityBreakdown(group, subcategory) {
    const groupRows = filteredDetailRecords.filter(r =>
      (r.staff_group === group || r.staff_group_name === group) &&
      ((r.subcategory === subcategory) || (r.subcategory_name === subcategory) || (r.subcategory_label === subcategory))
    );
    // derive categories from available detail rows else fallback to master list
    const cats = Array.from(new Set(filteredDetailRecords.map(r => r.category || r.category_name))).filter(Boolean);
    const useCats = cats.length ? cats : MASTER_CATEGORIES;
    return useCats.map(cat => {
      const byCat = (predGender) =>
        groupRows
          .filter(r => (r.category === cat || r.category_name === cat) && predGender(r))
          .reduce((sum, r) => {
            // prefer per-gender filled_count; fallback to aggregated fields if any
            const v = Number(r.filled_count ?? r.count ?? r.value ?? 0)
              || Number(r.male_count ?? r.female_count ?? r.transgender_count ?? 0);
            return sum + (isNaN(v) ? 0 : v);
          }, 0);
      const male = byCat(r => (r.gender === "Male" || r.gender_name === "Male"));
      const female = byCat(r => (r.gender === "Female" || r.gender_name === "Female"));
      const transgender = byCat(r => (r.gender === "Transgender" || r.gender_name === "Transgender"));
      return { category: cat, male, female, transgender };
    });
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      {/* Teaching table + preview */}
      {activeTab === "teaching" && (
        <div className="rounded-3xl shadow-xl border border-blue-100 overflow-x-auto bg-white">
          <table className="min-w-full text-base text-gray-800">
            <thead className="bg-gradient-to-r from-blue-100 to-indigo-100">
              <tr>
                {/* NEW: S.No */}
                <th className="px-4 py-3 font-bold tracking-wider text-blue-700">S.No</th>
                <th className="px-4 py-3 font-bold tracking-wider text-blue-700">Name</th>
                <th className="px-4 py-3 font-bold tracking-wider text-blue-700">Department</th>
                <th className="px-4 py-3 font-bold tracking-wider text-blue-700">Designation</th>
                <th className="px-4 py-3 font-bold tracking-wider text-blue-700">Gender</th>
                <th className="px-4 py-3 font-bold tracking-wider text-blue-700">Date of Joining</th>
                <th className="px-4 py-3 font-bold tracking-wider text-blue-700">Preview</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTeaching.length > 0 ? paginatedTeaching.map((row, idx) => (
                <tr key={row.id || idx} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition border-b border-blue-50 last:border-0">
                  {/* NEW: S.No cell */}
                  <td className="px-4 py-3">{serialBase + idx + 1}</td>
                  <td className="px-4 py-3">{row.name}</td>
                  <td className="px-4 py-3">{row.department}</td>
                  <td className="px-4 py-3">{row.designation}</td>
                  <td className="px-4 py-3">{row.gender}</td>
                  <td className="px-4 py-3">{formatDate(row.date_of_joining)}</td>
                  <td className="px-4 py-3">
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold" onClick={() => setPreviewRow(row)}>Preview</button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400 bg-gradient-to-r from-blue-50 to-indigo-50">No teaching staff records found</td>
                </tr>
              )}
            </tbody>
          </table>
          {renderTeachingPagination()}
        </div>
      )}

      {/* Preview Modal */}
      {previewRow && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-8 max-w-3xl w-full mx-4 relative overflow-y-auto" style={{ maxHeight: "80vh" }}>
            <button className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-2xl" onClick={() => setPreviewRow(null)}>&times;</button>
            <h3 className="text-xl font-bold mb-6 text-blue-700 text-center">Teaching Staff Details Preview</h3>

            {/* Profile */}
            <div className="flex justify-center mb-6">
              <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center shadow">
                <svg className="w-12 h-12 text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M6 20c0-2.2 3.6-3.5 6-3.5s6 1.3 6 3.5" />
                </svg>
              </div>
            </div>

            {/* REPLACED: use proper table layout (row/column) */}
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                <tbody className="text-sm">
                  <tr className="bg-gray-50">
                    <th className="px-4 py-2 text-left font-semibold w-1/4">Name</th>
                    <td className="px-4 py-2 w-1/4">{previewRow.name || "-"}</td>
                    <th className="px-4 py-2 text-left font-semibold w-1/4">Department</th>
                    <td className="px-4 py-2 w-1/4">{previewRow.department || "-"}</td>
                  </tr>
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold">Designation</th>
                    <td className="px-4 py-2">{previewRow.designation || "-"}</td>
                    <th className="px-4 py-2 text-left font-semibold">Gender</th>
                    <td className="px-4 py-2">{previewRow.gender || "-"}</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-2 text-left font-semibold">Date of Birth</th>
                    <td className="px-4 py-2">{formatDate(previewRow.date_of_birth)}</td>
                    <th className="px-4 py-2 text-left font-semibold">Email</th>
                    <td className="px-4 py-2">{previewRow.email || "-"}</td>
                  </tr>
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold">Mobile No</th>
                    <td className="px-4 py-2">{previewRow.mobile_no || "-"}</td>
                    <th className="px-4 py-2 text-left font-semibold">PAN Number</th>
                    <td className="px-4 py-2">{previewRow.pan_number || "-"}</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-2 text-left font-semibold">Nature of Appointment</th>
                    <td className="px-4 py-2">{previewRow.nature_of_appointment || "-"}</td>
                    <th className="px-4 py-2 text-left font-semibold">Social Category</th>
                    <td className="px-4 py-2">{previewRow.social_category || "-"}</td>
                  </tr>
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold">Religious Community</th>
                    <td className="px-4 py-2">{previewRow.religious_community || "-"}</td>
                    <th className="px-4 py-2 text-left font-semibold">PWBD</th>
                    <td className="px-4 py-2">{previewRow.pwbd_status ? "Yes" : "No"}</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-2 text-left font-semibold">Date of Joining</th>
                    <td className="px-4 py-2">{formatDate(previewRow.date_of_joining)}</td>
                    <th className="px-4 py-2 text-left font-semibold">Date of Joining Profession</th>
                    <td className="px-4 py-2">{formatDate(previewRow.date_of_joining_profession)}</td>
                  </tr>
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold">Job Status</th>
                    <td className="px-4 py-2">{previewRow.job_status || "-"}</td>
                    <th className="px-4 py-2 text-left font-semibold">Date of Leaving</th>
                    <td className="px-4 py-2">{formatDate(previewRow.date_of_leaving)}</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-2 text-left font-semibold">Date of Status Change</th>
                    <td className="px-4 py-2">{formatDate(previewRow.date_of_status_change)}</td>
                    <th className="px-4 py-2 text-left font-semibold">Highest Qualification</th>
                    <td className="px-4 py-2">{previewRow.highest_qualification || "-"}</td>
                  </tr>
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold">Programme Highest Qualification</th>
                    <td className="px-4 py-2">{previewRow.programme_highest_qualification || "-"}</td>
                    <th className="px-4 py-2 text-left font-semibold">Broad Discipline Group Name</th>
                    <td className="px-4 py-2">{previewRow.broad_discipline_group_name || "-"}</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-2 text-left font-semibold">Broad Discipline Group Category</th>
                    <td className="px-4 py-2">{previewRow.broad_discipline_group_category || "-"}</td>
                    <th className="px-4 py-2 text-left font-semibold">Additional Qualification</th>
                    <td className="px-4 py-2">
                      {Array.isArray(previewRow.additional_qualification)
                        ? previewRow.additional_qualification.join(", ")
                        : (previewRow.additional_qualification || "-")}
                    </td>
                  </tr>
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold">Other Than Teaching</th>
                    <td className="px-4 py-2">
                      Year {previewRow.year_spent_other_than_teaching || 0} Month {previewRow.month_spent_other_than_teaching || 0}
                    </td>
                    <th className="px-4 py-2 text-left font-semibold">Academic Year</th>
                    <td className="px-4 py-2">{previewRow.academic_year || "-"}</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-2 text-left font-semibold">Country Name</th>
                    <td className="px-4 py-2">{previewRow.country_name || "-"}</td>
                    <th className="px-4 py-2 text-left font-semibold"></th>
                    <td className="px-4 py-2"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {/* ...existing code... */}
    </div>
  );
}